import { useState, useEffect } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { partialRight } from 'lodash';

import WosResizeObserver from 'utils/observer_util';
import { handleConfigurationRouteChange } from 'utils/routing';

import navigation from 'domains/navigation';
import canvasModule from 'domains/canvases';

import type { RootState, AppDispatch } from 'store/wos_store';
import type { LGRootState, LGAppDispatch } from 'store/link_generator_store';
import type { NavigationHistoryState } from 'domains/navigation';
import type { CheckoutRootState } from 'store/checkout_store';
import type { CheckoutStatus } from 'domains/checkout';

// Need to use these typed hooks throughout app instead of Redux ones
type DispatchFunc = () => AppDispatch;
export const useAppDispatch: DispatchFunc = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// For use with slices that are only in checkout store
export const useAppSelectorCheckout: TypedUseSelectorHook<CheckoutRootState> = useSelector;

// These are the typed dispatch/selector hooks for the linkGenerator store
type DispatchFuncLG = () => LGAppDispatch;
export const useAppDispatchLG: DispatchFuncLG = useDispatch;
export const useAppSelectorLG: TypedUseSelectorHook<LGRootState> = useSelector;

/** @deprecated */
export function useCheckoutDisclosureHandler(startOpen: boolean, status: CheckoutStatus) {
  const [isOpen, setIsOpen] = useState(startOpen);
  const [willClose, setWillClose] = useState(false);

  useEffect(() => {
    if (status === 'pending') {
      setWillClose(true);
    }

    if (status === 'error') {
      setWillClose(false);
    }

    if ((status === 'idle' || status === 'success') && willClose) {
      setWillClose(false);
      setIsOpen(false);
    }
  }, [status, willClose]);

  return { isOpen, setIsOpen };
}

export function useDebounce(value: number | string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]); // Only re-call effect if value or delay changes

  return debouncedValue;
}

// Returns the height & width of any given element
export function useResizeObserver(elementId: string) {
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });

  function handleResize(resizeObserverEntries: ResizeObserverEntry[]) {
    let width = 0;
    let height = 0;
    // Handle various browser/polyfill ResizeObserverEntry data structures
    // eslint-disable-next-line no-restricted-syntax
    for (const entry of resizeObserverEntries) {
      // Checking for instanceof Array due to bug in firefox versions < 82
      // https://github.com/mdn/browser-compat-data/issues/8730
      if (entry.contentBoxSize && entry.contentBoxSize instanceof Array) {
        width = entry.contentBoxSize[0].inlineSize;
        height = entry.contentBoxSize[0].blockSize;
      } else {
        width = entry.contentRect.width;
        height = entry.contentRect.height;
      }
    }

    setDimensions({ width, height });
  }

  useEffect(() => {
    const observer = new WosResizeObserver();
    const element = document.getElementById(elementId);

    if (element) {
      observer.observe(handleResize, element);
    }

    return () => {
      observer.disconnect();
    };
  }, [elementId]);

  return dimensions;
}

// Custom hook for use in WOS Provider for managing history states
export function useWosRouting() {
  const configPath = useAppSelector(navigation.selectors.canvasConfigPath);
  const configParams = useAppSelector(navigation.selectors.optionsQueryParam);
  const customSizeParam = useAppSelector(navigation.selectors.customSizeQueryParam);
  const canvasIds = useAppSelector(canvasModule.selectors.selectIds);
  const dispatch = useAppDispatch();

  // If the catalog/subcatalog/product changes, insert a new history item
  // Don't try to do this if we have multiple canvases, gets too noisy
  useEffect(() => {
    if (canvasIds.length > 1) return;
    handleConfigurationRouteChange(configPath, dispatch, navigation.actions.navigateTo);
  }, [configPath, canvasIds, dispatch]);

  // If any options change, replace the previous history item
  useEffect(() => {
    const partialNavigate = partialRight(navigation.actions.navigateTo, null, true);
    handleConfigurationRouteChange(configParams, dispatch, partialNavigate);
  }, [configParams, dispatch]);

  // If custom size exists and changes, replace previous history item
  useEffect(() => {
    const partialNavigate = partialRight(navigation.actions.navigateTo, null, true);
    handleConfigurationRouteChange(customSizeParam, dispatch, partialNavigate);
  }, [customSizeParam, dispatch]);

  // Handle back/forward state
  useEffect(() => {
    function handlePopState(e: PopStateEvent) {
      const target = e.target as Window;
      const state: NavigationHistoryState | null = e.state;
      const nextRoute = state ? state.route : null;
      dispatch(navigation.actions.navigateTo(nextRoute, target));
    }

    window.addEventListener('popstate', handlePopState);

    return function cleanup() {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
